import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LOCAL_STORAGE } from '@ng-web-apis/common';
import { UserService } from '../../../../common/user/user.service';
import { OpenSnackBarService } from '../../../../common/open-snack-bar/open-snack-bar.service';
import { Capacitor } from '@capacitor/core';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';

export type SupportedLang = 'it' | 'en' | 'es' | 'fr' | 'de';
export const SUPPORTED_LANGS: SupportedLang[] = ['it', 'en', 'es', 'fr', 'de'];

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private promptInProgress = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(LOCAL_STORAGE) private localStorage: Storage,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    private userService: UserService,
    private openSnackBarService: OpenSnackBarService,
  ) {}

  getExplicitPreferredLanguage(): SupportedLang | null {
    if (this.userService.user?.favouriteLanguage) {
      const userLang = this.userService.user.favouriteLanguage.toLowerCase();
      if (SUPPORTED_LANGS.includes(userLang as SupportedLang)) {
        return userLang as SupportedLang;
      }
    }
    if (isPlatformBrowser(this.platformId)) {
      const stored = this.localStorage.getItem('sharryFavouriteLanguage');
      if (stored && SUPPORTED_LANGS.includes(stored as SupportedLang)) {
        return stored as SupportedLang;
      }

      const browserLang = navigator.language.split('-')[0];
      if (SUPPORTED_LANGS.includes(browserLang as SupportedLang)) {
        return browserLang as SupportedLang;
      }
    }
    return null;
  }

  extractLangFromUrl(url: string): SupportedLang | null {
    const match = url?.match(/^\/([a-z]{2})(\/|$)/);
    const lang = match?.[1];
    return lang && SUPPORTED_LANGS.includes(lang as SupportedLang) ? (lang as SupportedLang) : null;
  }

  checkAndPrompt(urlLang: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve();

    const normalizedUrlLang = urlLang?.toLowerCase();
    if (!SUPPORTED_LANGS.includes(normalizedUrlLang as SupportedLang)) return Promise.resolve();

    const preferredLang = this.getExplicitPreferredLanguage();
    if (!preferredLang || preferredLang === normalizedUrlLang) return Promise.resolve();

    if (this.promptInProgress) return Promise.resolve();

    this.promptInProgress = true;

    const i18nextInstance = (this.translateService as any).i18next;

    const openSnackbar = () => {
      import('../../../components/language-snack-bar/language-snack-bar.component').then(
        ({ LanguageSnackBarComponent }) => {
          const snackBarRef = this.openSnackBarService.openSnackBar(LanguageSnackBarComponent, {
            data: {
              text: 'translate-snackbar.text',
              favouriteLanguage: preferredLang,
            },
            panelClass:
              isPlatformBrowser(this.platformId) && !Capacitor.isNativePlatform()
                ? ['language-snack-bar__position']
                : [],
          });

          snackBarRef.afterDismissed().subscribe(() => {
            this.promptInProgress = false;
          });
        },
      );
    };

    return new Promise<void>(resolve => {
      if (i18nextInstance?.reloadResources) {
        i18nextInstance.reloadResources([preferredLang], undefined, (err: any) => {
          if (err) {
            console.error('LanguageService: reloadResources error', err);
          }
          openSnackbar();
          resolve();
        });
      } else {
        // fallback: nessun metodo di caricamento disponibile, apri comunque lo snackbar
        openSnackbar();
        resolve();
      }
    });
  }
}
